import React, { useEffect, useRef, useState } from "react";
import * as d3 from "d3";
import { useInViewOnce } from "../utils/useInViewOnce";
import "./ThickBarChart.scss";

interface BarDatum {
  label: string;
  value: number;
  color: string;
}

interface ThickBarChartProps {
  data: BarDatum[];
  title: string;
  subtitle: string;
  width?: number;
  height?: number;
  maxValue?: number;
  className?: string;
}

const ThickBarChart: React.FC<ThickBarChartProps> = ({
  data,
  title,
  subtitle,
  width = 600,
  height = 300,
  maxValue,
  className,
}) => {
  // trigger as soon as any part of the element is visible
  const { ref, inView } = useInViewOnce<SVGSVGElement>({
    threshold: 0,
    rootMargin: "0px",
  });
  const [progress, setProgress] = useState(0);
  const [showValues, setShowValues] = useState(false);
  const rafRef = useRef<number | null>(null);
  const startTimeRef = useRef<number | null>(null);
  const durationMs = 800;

  const isResponsive =
    !!className && className.split(" ").includes("responsive-chart");

  useEffect(() => {
    if (!inView) return;
    // animate once when in view
    const step = (ts: number) => {
      if (startTimeRef.current === null) startTimeRef.current = ts;
      const elapsed = ts - startTimeRef.current;
      const t = Math.min(1, elapsed / durationMs);
      // easeOutCubic
      const eased = 1 - Math.pow(1 - t, 3);
      setProgress(eased);
      if (t < 1) {
        rafRef.current = requestAnimationFrame(step);
      }
    };
    rafRef.current = requestAnimationFrame(step);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [inView]);

  useEffect(() => {
    if (progress >= 0.999) {
      // allow next paint then fade in values
      const id = requestAnimationFrame(() => setShowValues(true));
      return () => cancelAnimationFrame(id);
    }
  }, [progress]);
  const margin = { top: 20, right: 30, bottom: 40, left: 30 };
  const chartWidth = width - margin.left - margin.right;
  const chartHeight = height - margin.top - margin.bottom;

  const maxVal = maxValue || d3.max(data, (d) => d.value) || 100;
  const yScale = d3.scaleLinear().domain([0, maxVal]).range([chartHeight, 0]);

  const xScale = d3
    .scaleBand()
    .domain(data.map((d) => d.label))
    .range([0, chartWidth])
    .padding(0.2);

  const barWidth = xScale.bandwidth();

  return (
    <div className={`thick-bar-chart ${className || ""}`}>
      <div className="chart-header">
        <h3 className="chart-title">{title}</h3>
        <p className="chart-subtitle">{subtitle}</p>
      </div>

      <svg
        {...(isResponsive ? {} : { width, height })}
        viewBox={`0 0 ${width} ${height}`}
        ref={ref}
      >
        <g transform={`translate(${margin.left},${margin.top})`}>
          {/* Y-axis */}
          <g className="y-axis">
            {yScale.ticks(8).map((tick) => (
              <g key={tick} transform={`translate(0,${yScale(tick)})`}>
                <line x2={chartWidth} stroke="#e0e0e0" strokeWidth={1} />
                <text
                  x={-10}
                  y={0}
                  textAnchor="end"
                  alignmentBaseline="middle"
                  className="y-axis-text"
                  fill="#666"
                >
                  {tick}
                </text>
              </g>
            ))}
          </g>

          {/* Bars */}
          {data.map((d, i) => {
            const fullHeight = chartHeight - yScale(d.value);
            const animatedHeight = fullHeight * progress;
            const animatedY = yScale(d.value) + (fullHeight - animatedHeight);

            return (
              <g key={d.label}>
                <rect
                  x={xScale(d.label) || 0}
                  y={animatedY}
                  width={barWidth}
                  height={animatedHeight}
                  fill={d.color}
                />

                {/* Value labels on bars */}
                <text
                  x={(xScale(d.label) || 0) + barWidth / 2}
                  y={animatedY - 8}
                  textAnchor="middle"
                  className="value-text"
                  fontWeight={600}
                  fill="#333"
                  style={{
                    opacity: showValues ? 1 : 0,
                    transition: "opacity 300ms ease",
                  }}
                >
                  {d.value}%
                </text>
              </g>
            );
          })}

          {/* X-axis labels */}
          <g className="x-axis">
            {data.map((d) => (
              <text
                key={d.label}
                x={(xScale(d.label) || 0) + barWidth / 2}
                y={chartHeight + 20}
                textAnchor="middle"
                className="x-axis-text"
                fill="#333"
                fontWeight={500}
              >
                {d.label}
              </text>
            ))}
          </g>
        </g>
      </svg>
    </div>
  );
};

export default ThickBarChart;
